<template>
  <div ref="containerRef" class="tw:md:hidden tw:flex tw:flex-col tw:md:flex-row tw:w-vw tw:ml-6 tw:overflow-x-clip">
    <div
      class="rc_carousel-mobile tw:whitespace-nowrap tw:transition-transform tw:duration-1000"
      ref="inner"
      :style="innerStyles"
    >
      <div ref="boxesRef" v-for="item in cards" :key="item.id" :id="item.id" class="tw:mr-[18px] tw:inline-flex">
        <RateConnectBenefitsCard :item="item" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onUpdated, nextTick, onMounted, onBeforeUnmount } from 'vue'

import TouchEvent from '@/utils/touchEvents.ts'
import RateConnectBenefitsCard from '@/components/productPage/rateConnect/benefits/RateConnectBenefitsCard.vue'

import { useSessionValues } from '@/composables/useSessionValues'

const emit = defineEmits(['onCardChanged'])

const props = defineProps({
  cards: {
    type: Array,
    required: true,
  },
})

const { language } = useSessionValues()
const boxesRef = ref([])
const containerRef = ref(null)
const touchEvent = ref(null)
const timer = ref('')
const inner = ref(null)
const innerStyles = ref({})
const step = ref('')
const transitioning = ref(false)

const setEqualHeight = () => {
  const boxes = boxesRef.value
  if (!boxes || boxes.length === 0) return

  boxes.forEach(box => {
    box.style.height = 'auto'
  })

  let maxHeight = 0
  boxes.forEach(box => {
    if (box.offsetHeight > maxHeight) {
      maxHeight = box.offsetHeight
    }
  })

  boxes.forEach(box => {
    box.style.height = maxHeight + 'px'
  })
}

const autoplay = () => {
  next()
  timer.value = setTimeout(autoplay, 5000)
}

const moveLeft = () => {
  innerStyles.value = {
    transition: 'transform 0.5s ease',
    transform: `translateX(-${step.value}px)`,
  }
}

const moveRight = () => {
  innerStyles.value = {
    transition: 'transform 0.5s ease',
    transform: `translateX(${step.value}px)`,
  }
}

const next = () => {
  if (transitioning.value) return
  transitioning.value = true
  moveLeft()
  afterTransition(() => {
    const firstElement = containerRef.value.querySelector('.rc_carousel-mobile')?.firstElementChild
    containerRef.value.querySelector('.rc_carousel-mobile').appendChild(firstElement)

    updateCurrent()
    resetTranslate()
    transitioning.value = false
  })
}

const afterTransition = callback => {
  const listener = () => {
    callback()
    inner.value?.removeEventListener('transitionend', listener)
  }
  inner.value?.addEventListener('transitionend', listener)
}

const resetTranslate = () => {
  innerStyles.value = {
    transition: 'none',
    transform: 'translateX(0)',
  }
}

const setStep = () => {
  const innerWidth = inner.value.scrollWidth
  const totalCards = props.cards.length

  step.value = innerWidth / totalCards
}

const prev = () => {
  if (transitioning.value) return
  transitioning.value = true
  moveRight()
  afterTransition(() => {
    const lastElement = containerRef.value.querySelector('.rc_carousel-mobile').lastElementChild
    containerRef.value.querySelector('.rc_carousel-mobile').prepend(lastElement)

    updateCurrent()
    resetTranslate()
    transitioning.value = false
  })
}

const handleSwipe = event => {
  if (!touchEvent.value) {
    return
  }
  touchEvent.value.setEndEvent(event)

  if (touchEvent.value.isSwipeRight()) {
    prev()
  } else if (touchEvent.value.isSwipeLeft()) {
    next()
  }
  touchEvent.value = null
}

const updateCurrent = () => {
  const firstElementId = containerRef.value.querySelector('.rc_carousel-mobile').firstElementChild.id
  emit('onCardChanged', { id: firstElementId })
}

watch(language, () => {
  nextTick(() => {
    setEqualHeight()
  })
})

onMounted(() => {
  setEqualHeight()
  setStep()
  autoplay()
  document.addEventListener('touchstart', event => {
    touchEvent.value = new TouchEvent(event)
  })

  document.addEventListener('touchend', handleSwipe)
  window.addEventListener('resize', setEqualHeight)
})

onBeforeUnmount(() => {
  clearInterval(timer)
  window.removeEventListener('resize', setEqualHeight)
  document.removeEventListener('touchend', handleSwipe)
})

onUpdated(() => {
  setEqualHeight()
})
</script>
